import { ChangeEvent, useCallback, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { getAllBranch, getAllRoutes, getAllRoutesByCompany } from "../../services";
import { SaleRoute } from "../../types/sale-route.type";
import { Branch } from "../../types/user.type";
import FilterField, {
  FILTER_ROW_BOTTOM_SPACING,
  FLOATING_HELPER_TEXT_SX,
} from "../SaleDepositReport/FilterField";
import { EARLIEST_REPORT_MONTH } from "./constants";

/** The ทุกสาย option: no route is every route of the branch. */
const ALL_ROUTES_OPTION = "all";
const ALL_ROUTES_LABEL = "ทุกสาย";
/** The branch picker's value until head office picks one. */
const NO_BRANCH_OPTION = "";
const PICK_BRANCH_LABEL = "เลือกสาขา";
const PICK_BRANCH_FIRST_TEXT = "เลือกสาขาก่อน";

const PICKER_LOCALE = "th";
const MONTH_VIEWS = ["year", "month"] as const;
const MONTH_DISPLAY_FORMAT = "MMMM YYYY";
const EARLIEST_MONTH = dayjs(EARLIEST_REPORT_MONTH);
const MONTH_PICKER_SLOT_PROPS = {
  textField: { size: "small", fullWidth: true },
} as const;
const BRANCH_SELECT_SLOT_PROPS = { select: { displayEmpty: true } } as const;

interface WastePercentFiltersProps {
  month: Dayjs;
  onChangeMonth: (month: Dayjs) => void;
  canPickBranch: boolean;
  branchCode: string | null;
  onSelectBranch: (branchCode: string) => void;
  routeUuid: string | null;
  onSelectRoute: (routeUuid: string | null) => void;
}

/**
 * เดือน, then สาขา for an all-branch reader, then สายส่ง. A branch reader's routes are their own
 * branch's; head office picks a branch first and is offered that branch's routes.
 */
export default function WastePercentFilters({
  month,
  onChangeMonth,
  canPickBranch,
  branchCode,
  onSelectBranch,
  routeUuid,
  onSelectRoute,
}: Readonly<WastePercentFiltersProps>) {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [routes, setRoutes] = useState<SaleRoute[]>([]);

  const loadBranches = useCallback(async () => {
    if (!canPickBranch) return;
    try {
      const { data } = await getAllBranch();
      const loaded: Branch[] = data.data ?? [];
      setBranches(loaded);
    } catch {
      // The interceptor already reported the failure; the picker stays empty.
      setBranches([]);
    }
  }, [canPickBranch]);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  // GET /routes filters by the branch's company_id, while the report names it by branch_code.
  const pickedCompanyId = branches.find(
    (branch) => branch.branch_code === branchCode,
  )?.company_id;
  const isRouteScopeKnown = !canPickBranch || pickedCompanyId !== undefined;

  useEffect(() => {
    if (!isRouteScopeKnown) return undefined;
    let isLatest = true;
    // A branch reader's own routes come from their token's branch.
    const request =
      pickedCompanyId === undefined
        ? getAllRoutesByCompany()
        : getAllRoutes(pickedCompanyId);
    // The previous branch's routes are not offered while the new branch's load.
    setRoutes([]);
    request
      .then(({ data }) => {
        const loaded: SaleRoute[] = data.data ?? [];
        if (isLatest) setRoutes(loaded);
      })
      .catch(() => {
        // The interceptor already reported the failure; the picker keeps only ทุกสาย.
        if (isLatest) setRoutes([]);
      });
    return () => {
      isLatest = false;
    };
  }, [isRouteScopeKnown, pickedCompanyId]);

  const handleChangeMonth = (value: Dayjs | null) => {
    // A half-typed month is not a month yet; the report keeps the last whole one.
    if (value?.isValid()) onChangeMonth(value);
  };

  const handleChangeBranch = (event: ChangeEvent<HTMLInputElement>) => {
    onSelectBranch(event.target.value);
  };

  const handleChangeRoute = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    onSelectRoute(value === ALL_ROUTES_OPTION ? null : value);
  };

  return (
    <Grid container spacing={2} mb={FILTER_ROW_BOTTOM_SPACING}>
      <FilterField label="เดือน">
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={PICKER_LOCALE}>
          <DatePicker
            views={MONTH_VIEWS}
            openTo="month"
            value={month}
            onChange={handleChangeMonth}
            format={MONTH_DISPLAY_FORMAT}
            minDate={EARLIEST_MONTH}
            disableFuture
            slotProps={MONTH_PICKER_SLOT_PROPS}
          />
        </LocalizationProvider>
      </FilterField>
      {canPickBranch && (
        <FilterField label="สาขา">
          <TextField
            select
            size="small"
            fullWidth
            value={branchCode ?? NO_BRANCH_OPTION}
            onChange={handleChangeBranch}
            slotProps={BRANCH_SELECT_SLOT_PROPS}
          >
            <MenuItem value={NO_BRANCH_OPTION} disabled>
              {PICK_BRANCH_LABEL}
            </MenuItem>
            {branches.map((branch) => (
              <MenuItem key={branch.uuid} value={branch.branch_code}>
                {branch.branch_code} - {branch.branch_name}
              </MenuItem>
            ))}
          </TextField>
        </FilterField>
      )}
      <FilterField label="สายส่ง">
        <TextField
          select
          size="small"
          fullWidth
          disabled={!isRouteScopeKnown}
          value={routeUuid ?? ALL_ROUTES_OPTION}
          onChange={handleChangeRoute}
          helperText={isRouteScopeKnown ? undefined : PICK_BRANCH_FIRST_TEXT}
          sx={FLOATING_HELPER_TEXT_SX}
        >
          <MenuItem value={ALL_ROUTES_OPTION}>{ALL_ROUTES_LABEL}</MenuItem>
          {routes.map((route) => (
            <MenuItem key={route.uuid} value={route.uuid}>
              {route.route_code} - {route.route_name}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
    </Grid>
  );
}
